import type { KycStatus, User } from '../types';
import { hasStoredSession, refreshSession, getRefreshToken, onSessionExpired } from '../api/client';
import {
  login as apiLogin,
  registerFarmer as apiRegisterFarmer,
  registerHtx as apiRegisterHtx,
  fetchMe,
  logout as apiLogout,
  updateMe,
  changePassword as apiChangePassword,
  zaloAuthUrl as apiZaloAuthUrl,
  zaloCallback as apiZaloCallback,
  zaloRegister as apiZaloRegister,
  zaloLinkUrl as apiZaloLinkUrl,
  zaloUnlink as apiZaloUnlink,
  type MeResponse,
  type BackendRole,
  type UpdateMePayload,
  type ZaloCallbackResult,
  type ZaloRegisterPayload,
} from '../api/auth';
import { getFarmerById, getHtxById, toFrontendKycStatus, toFrontendMembershipStatus } from '../api/ekyc';

export type AuthStatus = 'initializing' | 'authenticated' | 'unauthenticated';

export interface AuthState {
  status: AuthStatus;
  user: User | null;
}

let authState: AuthState = { status: 'initializing', user: null };
const listeners = new Set<() => void>();

function setAuthState(next: Partial<AuthState>) {
  authState = { ...authState, ...next };
  listeners.forEach((listener) => listener());
}

export function subscribeAuth(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getAuthState(): AuthState {
  return authState;
}

/** Back-office roles (CO/DEPT_HEAD/DIRECTOR/ACCOUNTANT/OPS/ADMIN) belong to the
 * separate Admin Portal BFF, not this farmer/HTX-facing app — they never map to a
 * usable role here. */
function mapRole(role: BackendRole): 'Nông hộ' | 'Hợp tác xã' | null {
  if (role === 'FARMER') return 'Nông hộ';
  if (role === 'HTX_STAFF') return 'Hợp tác xã';
  return null;
}

async function hydrateUser(me: MeResponse): Promise<User | null> {
  const role = mapRole(me.role);
  if (!role) return null;

  let kycStatus: KycStatus | undefined;
  let membershipStatus: User['membershipStatus'];
  if (me.farmerId) {
    try {
      const farmer = await getFarmerById(me.farmerId);
      kycStatus = toFrontendKycStatus(farmer.ekycStatus);
      membershipStatus = toFrontendMembershipStatus(farmer.membershipStatus);
    } catch {
      // profile lookup failed — leave kycStatus/membershipStatus undefined rather than blocking login
    }
  } else if (role === 'Nông hộ') {
    kycStatus = 'registered'; // self-registered farmer with no farmer profile yet
  } else if (role === 'Hợp tác xã' && me.htxId) {
    try {
      const htx = await getHtxById(me.htxId);
      kycStatus = toFrontendKycStatus(htx.ekycStatus ?? 'NOT_SUBMITTED');
    } catch {
      // profile lookup failed — leave kycStatus undefined rather than blocking login
    }
  }

  return {
    id: me.id,
    username: me.phone,
    role,
    displayName: me.fullName,
    code: me.phone,
    farmerId: me.farmerId,
    htxId: me.htxId,
    membershipStatus,
    kycStatus,
    zaloLinked: me.zaloLinked,
    avatarUrl: me.avatarUrl,
    language: me.language,
    fontSize: me.fontSize,
    notificationPrefs: me.notificationPrefs,
  };
}

export async function initAuth(): Promise<void> {
  if (!hasStoredSession()) {
    setAuthState({ status: 'unauthenticated', user: null });
    return;
  }
  const refreshed = await refreshSession();
  if (!refreshed) {
    setAuthState({ status: 'unauthenticated', user: null });
    return;
  }
  try {
    const me = await fetchMe();
    const user = await hydrateUser(me);
    setAuthState({ status: user ? 'authenticated' : 'unauthenticated', user });
  } catch {
    setAuthState({ status: 'unauthenticated', user: null });
  }
}

export async function login(phone: string, password: string, mfaCode?: string): Promise<User> {
  await apiLogin(phone, password, mfaCode);
  return adoptSession();
}

/** Loads the freshly-stored session into `authState`. Shared by every path that
 * obtains a token pair without a phone/password (Zalo login, Zalo sign-up). */
async function adoptSession(): Promise<User> {
  const me = await fetchMe();
  const user = await hydrateUser(me);
  if (!user) {
    throw new Error('Tài khoản này không có quyền truy cập ứng dụng Agrida');
  }
  setAuthState({ status: 'authenticated', user });
  return user;
}

export async function getZaloAuthUrl(): Promise<string> {
  const { url } = await apiZaloAuthUrl();
  return url;
}

/** Redeems the `?code=&state=` Zalo bounced back to /dang-nhap. Resolves to the
 * signed-in user, or to the pending-registration payload the caller must finish
 * through {@link zaloRegister}. */
export async function zaloCallback(
  code: string,
  state: string,
): Promise<
  | { user: User }
  | Extract<ZaloCallbackResult, { status: 'registration_required' } | { status: 'linked' }>
> {
  const result = await apiZaloCallback(code, state);
  if (result.status === 'authenticated') return { user: await adoptSession() };
  // 'linked' means the caller was already signed in and just attached Zalo —
  // re-read the session so the settings row flips without a reload.
  if (result.status === 'linked') {
    await adoptSession();
    return result;
  }
  return result;
}

/** Authenticated: start attaching Zalo to the current account. */
export async function getZaloLinkUrl(): Promise<string> {
  const { url } = await apiZaloLinkUrl();
  return url;
}

export async function unlinkZalo(): Promise<void> {
  await apiZaloUnlink();
  await adoptSession();
}

export async function zaloRegister(payload: ZaloRegisterPayload): Promise<User> {
  await apiZaloRegister(payload);
  return adoptSession();
}

export async function registerFarmer(data: { phone: string; password: string; fullName: string; htxId?: string }): Promise<void> {
  await apiRegisterFarmer(data);
}

export async function registerHtx(data: { name: string; code: string; address?: string; phone: string; password: string; fullName: string }): Promise<void> {
  await apiRegisterHtx(data);
}

export async function logout(): Promise<void> {
  const refreshToken = getRefreshToken();
  if (refreshToken) await apiLogout(refreshToken);
  setAuthState({ status: 'unauthenticated', user: null });
}

/** Called after a farmer completes eKYC self-onboarding (profile creation), so the
 * in-memory session reflects the new farmerId/kycStatus without a full re-login. */
/** Re-reads the session from the server. The eKYC waiting screen uses this to
 * check whether approval has landed, instead of reloading the whole page — a
 * reload drops the user back on the same screen with nothing to show that it
 * did anything. */
export async function reloadCurrentUser(): Promise<User> {
  return adoptSession();
}

export function refreshCurrentUser(patch: Partial<User>): void {
  if (!authState.user) return;
  setAuthState({ user: { ...authState.user, ...patch } });
}

/** Self-service profile update (Cài đặt → Thông tin tài khoản / thông báo / cỡ chữ /
 * ngôn ngữ) — PATCH /auth/me, then mirror the accepted fields onto the local session. */
export async function updateProfile(patch: UpdateMePayload): Promise<void> {
  const updated = await updateMe(patch);
  refreshCurrentUser({
    displayName: updated.fullName,
    avatarUrl: updated.avatarUrl,
    language: updated.language,
    fontSize: updated.fontSize,
    notificationPrefs: updated.notificationPrefs,
  });
}

/** POST /auth/change-password already rotates + stores the new token pair
 * (see api/auth.ts) — no local user-state change is needed here. */
export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await apiChangePassword(currentPassword, newPassword);
}

onSessionExpired(() => {
  setAuthState({ status: 'unauthenticated', user: null });
});
